import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ActivityLog, ActionType, EntityType } from '@/types/activity-log.types';
import { formatDistanceToNow } from 'date-fns';
import {
  Plus,
  Edit,
  Trash2,
  UserPlus,
  UserMinus,
  CheckCircle,
  Calendar,
  PartyPopper,
  CalendarDays,
  ListTodo,
  Users,
  Briefcase,
} from 'lucide-react';

interface ActivityItemProps {
  activity: ActivityLog;
  onEntityClick?: (entityType: EntityType, entityId: number) => void;
}

/**
 * ActivityItem Component
 * Renders a compact single activity log entry (max 2 lines)
 */
export const ActivityItem: React.FC<ActivityItemProps> = ({ activity, onEntityClick }) => {
  const navigate = useNavigate();

  const getActionIcon = (actionType: ActionType) => {
    const iconClass = 'w-3.5 h-3.5';
    switch (actionType) {
      case 'CREATE':
        return <Plus className={`${iconClass} text-green-600`} />;
      case 'UPDATE':
        return <Edit className={`${iconClass} text-blue-600`} />;
      case 'DELETE':
        return <Trash2 className={`${iconClass} text-red-600`} />;
      case 'ASSIGN':
        return <UserPlus className={`${iconClass} text-purple-600`} />;
      case 'UNASSIGN':
        return <UserMinus className={`${iconClass} text-orange-600`} />;
      case 'STATUS_CHANGE':
        return <CheckCircle className={`${iconClass} text-indigo-600`} />;
      case 'RSVP_CHANGE':
        return <Calendar className={`${iconClass} text-pink-600`} />;
      default:
        return <Plus className={`${iconClass} text-gray-600`} />;
    }
  };

  const getEntityIcon = (entityType: EntityType) => {
    const iconClass = 'w-3 h-3';
    switch (entityType) {
      case 'celebration':
        return <PartyPopper className={`${iconClass} text-purple-500`} />;
      case 'event':
        return <CalendarDays className={`${iconClass} text-blue-500`} />;
      case 'task':
        return <ListTodo className={`${iconClass} text-green-500`} />;
      case 'guest':
        return <Users className={`${iconClass} text-orange-500`} />;
      case 'team_member':
        return <UserPlus className={`${iconClass} text-indigo-500`} />;
      case 'activity':
        return <Calendar className={`${iconClass} text-pink-500`} />;
      case 'service':
        return <Briefcase className={`${iconClass} text-teal-500`} />;
      default:
        return null;
    }
  };

  const getActionColor = (actionType: ActionType) => {
    switch (actionType) {
      case 'CREATE':
        return 'bg-green-100';
      case 'UPDATE':
        return 'bg-blue-100';
      case 'DELETE':
        return 'bg-red-100';
      case 'ASSIGN':
        return 'bg-purple-100';
      case 'UNASSIGN':
        return 'bg-orange-100';
      case 'STATUS_CHANGE':
        return 'bg-indigo-100';
      case 'RSVP_CHANGE':
        return 'bg-pink-100';
      default:
        return 'bg-gray-100';
    }
  };

  const getEntityRoute = (entityType: EntityType, entityId: number): string | null => {
    switch (entityType) {
      case 'celebration':
        return `/celebrations/${entityId}`;
      case 'event':
        return `/events/${entityId}`;
      case 'task':
        return `/tasks`;
      case 'guest':
        return `/guests`;
      case 'team_member':
        return `/celebrations/${activity.celebration_id}`;
      case 'activity':
        return `/activities/${entityId}`;
      case 'service':
        return `/services`;
      default:
        return null;
    }
  };

  const handleEntityClick = (e: React.MouseEvent) => {
    e.preventDefault();
    const route = getEntityRoute(activity.entity_type, activity.entity_id);
    if (route) {
      if (onEntityClick) {
        onEntityClick(activity.entity_type, activity.entity_id);
      } else {
        navigate(route);
      }
    }
  };

  const formatTimestamp = (timestamp: string) => {
    try {
      return formatDistanceToNow(new Date(timestamp), { addSuffix: true });
    } catch {
      return 'recently';
    }
  };

  // Parse message to extract actor, action, and entity name
  const parseMessage = () => {
    const parts = activity.message.split(' ');
    const actorName = activity.actor_name || 'Someone';
    
    // Find the action verb (created, updated, deleted, etc.)
    let actionIndex = -1;
    const actionVerbs = ['created', 'updated', 'deleted', 'assigned', 'unassigned', 'changed', 'added', 'removed'];
    for (let i = 0; i < parts.length; i++) {
      if (actionVerbs.some(verb => parts[i].toLowerCase().includes(verb))) {
        actionIndex = i;
        break;
      }
    }

    if (actionIndex === -1) {
      return { actor: actorName, action: '', entityText: activity.message };
    }

    const action = parts.slice(actionIndex).join(' ');
    return { actor: actorName, action, entityText: activity.entity_name || '' };
  };

  const { actor, action, entityText } = parseMessage();

  return (
    <div className="flex items-start gap-2 px-3 py-2 hover:bg-gray-50 transition-colors">
      {/* Action Icon - Small */}
      <div className={`flex-shrink-0 p-1.5 rounded-full ${getActionColor(activity.action_type)}`}>
        {getActionIcon(activity.action_type)}
      </div>

      {/* Content - Compact */}
      <div className="flex-1 min-w-0">
        {/* Line 1: Actor + Action + Entity (with link) */}
        <div className="text-[11px] text-gray-900 leading-tight">
          <span className="font-medium">{actor}</span>
          {' '}
          <span className="text-gray-600">{action.split(' ').slice(0, -1).join(' ')}</span>
          {' '}
          <button
            onClick={handleEntityClick}
            className="text-blue-600 hover:text-blue-800 hover:underline font-medium inline-flex items-center gap-1"
          >
            {getEntityIcon(activity.entity_type)}
            '{entityText}'
          </button>
        </div>

        {/* Line 2: Breadcrumb + Timestamp */}
        <div className="flex items-center gap-1.5 mt-0.5 text-[10px] text-gray-500">
          {activity.parent_entity_type && (
            <>
              <span>{activity.parent_entity_type} → {activity.entity_type}</span>
              <span>•</span>
            </>
          )}
          <span>{formatTimestamp(activity.created_at)}</span>
        </div>
      </div>
    </div>
  );
};
